import {lazy, Suspense, type ComponentType, type ReactNode} from "react";
import {createBrowserRouter, Navigate, Outlet, useParams, useSearchParams, type RouteObject} from "react-router";
import {LandingPage} from "../pages/onboarding/LandingPage";
import {DOCS, type DocKey} from "../pages/app/docs";
import {PATHS} from "./paths";
import {GuestRoute} from "./GuestRoute";
import {TabShellLayout} from "../components/Main/TabShell";
import {ProtectedRoute, YandexOAuthGate} from "./ProtectedRoute";
import {OAuthRegistrationGate} from "./OAuthRegistrationGate";
import {needsOauthRegistration} from "./oauthRegistration";
import {useAuth} from "../contexts/AuthContext";
import {CompanyAuthLayout, CompanyGuestRoute, CompanyProtectedRoute} from "./CompanyRoute";
import {RouteLoader, TabRouteLoader} from "./RouteLoader";
import {RouteErrorBoundary} from "./RouteErrorBoundary";
import {ScrollToTop} from "./ScrollToTop";

/**
 * Страницы, кроме лендинга, грузятся отдельными чанками: раньше всё приложение
 * (включая админку, регистрацию и страницу скана) лежало в стартовом бандле, и
 * браузер разбирал его целиком до первой отрисовки.
 * Импорт указывает на файл страницы напрямую, а не на бочку pages/index —
 * иначе один общий модуль снова стянул бы все страницы в один чанк.
 */
const Swipes = lazy(() => import("../pages/app/Swipes").then((m) => ({default: m.Swipes})));
const Chats = lazy(() => import("../pages/app/Chats").then((m) => ({default: m.Chats})));
const InvitePage = lazy(() => import("../pages/app/InvitePage").then((m) => ({default: m.InvitePage})));
const ScanProfile = lazy(() => import("../pages/app/ScanProfile").then((m) => ({default: m.ScanProfile})));
const PaymentConfirmPage = lazy(() => import("../pages/app/PaymentConfirmPage").then((m) => ({default: m.PaymentConfirmPage})));
const SupportPage = lazy(() => import("../pages/app/SupportPage").then((m) => ({default: m.SupportPage})));
const RegisterPage = lazy(() => import("../pages/onboarding/RegisterPage").then((m) => ({default: m.RegisterPage})));
const About = lazy(() => import("../pages/onboarding/About"));
const AdminStandalonePage = lazy(() => import("../pages/admin/AdminStandalonePage").then((m) => ({default: m.AdminStandalonePage})));
const CompanyLoginPage = lazy(() => import("../pages/company/CompanyLoginPage").then((m) => ({default: m.CompanyLoginPage})));
const CompanyRegisterPage = lazy(() => import("../pages/company/CompanyRegisterPage").then((m) => ({default: m.CompanyRegisterPage})));
const CompanyDashboardPage = lazy(() => import("../pages/company/CompanyDashboardPage").then((m) => ({default: m.CompanyDashboardPage})));
const CompanyPlaceFormPage = lazy(() => import("../pages/company/CompanyPlaceFormPage").then((m) => ({default: m.CompanyPlaceFormPage})));
const CompanyPlacementStatusPage = lazy(() =>
    import("../pages/company/CompanyPlacementStatusPage").then((m) => ({default: m.CompanyPlacementStatusPage})),
);
const LegalDocumentPage = lazy(() => import("../pages/app/docs").then((m) => ({default: m.LegalDocumentPage})));
const WhereToGoPage = lazy(() => import("../pages/app/WhereToGoPage").then((m) => ({default: m.WhereToGoPage})));
const ProfilePage = lazy(() => import("../pages/app/ProfilePage").then((m) => ({default: m.ProfilePage})));
const AnalysisHistoryPage = lazy(() => import("../pages/app/AnalysisHistoryPage").then((m) => ({default: m.AnalysisHistoryPage})));

/** Прямая ссылка на разбор (issue #1066) → лента с открытой страницей разбора. */
function AnalysisDeepLink() {
    const {userId = ""} = useParams();
    const [params] = useSearchParams();
    return <Navigate to={PATHS.appAnalysis.inFeed(userId, params.get("view"))} replace/>;
}

/**
 * Визард регистрации для входа через VK ID / Яндекс ID (#1279): без шага email/пароля.
 * Анкета уже заполнена (или аккаунт не OAuth) — делать здесь нечего, уходим в приложение.
 */
function CompleteRegistrationRoute() {
    const {user} = useAuth();
    if (!needsOauthRegistration(user)) return <Navigate to={PATHS.app} replace/>;
    return page(<RegisterPage oauthMode/>);
}

/** Ленивая страница: индикатор на время загрузки чанка и экран ошибки, если он не приехал. */
function page(element: ReactNode, fallback: ReactNode = <RouteLoader/>): ReactNode {
    return (
        <RouteErrorBoundary>
            <Suspense fallback={fallback}>{element}</Suspense>
        </RouteErrorBoundary>
    );
}

/** Та же ленивая страница, но фолбэком — TabRouteLoader: экраны вкладок
 *  нижней навигации держат каркас (шапка + навигация) на месте на время
 *  холодной загрузки чанка, вместо подмены всего экрана (см. RouteLoader.tsx). */
function tabPage(element: ReactNode): ReactNode {
    return page(element, <TabRouteLoader/>);
}

/** Лендинг и онбординг: публичные страницы входа в сервис. */
const onboardingRoutes: RouteObject[] = [
    // Лендинг под гостевым гардом: с живой сессией сразу уходим в приложение.
    // Грузится не лениво — это первый экран, ждать отдельный чанк незачем.
    // /reset-password — тот же лендинг: ссылка из письма открывает модалку
    // нового пароля поверх него (useLandingHelpers видит ?token= в адресе).
    {element: <GuestRoute/>, children: [
        // page() вместо голого <LandingPage/>: внутри лендинга lazy-секции,
        // и если чанк падает при холодной загрузке, без errorElement React Router
        // показывает сырой «Unexpected Application Error!» (скрин Ирины 22:08,
        // issue #667 п.1/2). RouteErrorBoundary даёт аккуратный экран с ретраем.
        {index: true, element: page(<LandingPage/>)},
        {path: PATHS.resetPassword, element: page(<LandingPage/>)},
    ]},
    {path: PATHS.about, element: page(<About/>)},
    // /register — тоже под гостевым гардом (баг из ревью Ирины 20.08): раньше
    // страница открывалась и для уже залогиненной сессии (например, старые
    // токены из предыдущего теста в этом же браузере не разлогинились), и
    // человек проходил визард заново, думая, что создаёт новый аккаунт, хотя
    // на самом деле оставался залогинен под старым — выглядело как «вход без
    // подтверждения email». GuestRoute уводит такую сессию в приложение сразу,
    // как и с лендинга, вместо того чтобы показывать форму регистрации поверх
    // чужой/старой живой сессии.
    {element: <GuestRoute/>, children: [{path: PATHS.register, element: page(<RegisterPage/>)}]},
];

/** Приложение — только для авторизованных, под общей обёрткой YandexOAuthGate + ProtectedRoute. */
const appRoutes: RouteObject[] = [
    {
        element: <YandexOAuthGate><ProtectedRoute/></YandexOAuthGate>,
        children: [
            // Вкладки нижней навигации делят один каркас (шапка + таб-бар): он не
            // пересоздаётся при переходах между ними (см. TabShell.tsx).
            // #1279: OAuth-аккаунт без анкеты — сначала визард /register/complete.
            {
                element: <OAuthRegistrationGate/>,
                children: [
                    {
                        element: <TabShellLayout/>,
                        children: [
                            {
                                path: PATHS.app,
                                children: [
                                    {index: true, element: tabPage(<Swipes/>)},
                                    {path: PATHS.appChats, element: tabPage(<Chats/>)},
                                    {path: PATHS.appProfile, element: tabPage(<ProfilePage/>)},
                                    // Issue #676 п.11: прямой заход по ссылке /app/where-to-go раньше
                                    // не имел маршрута — catch-all отдавал пустой редирект на /app,
                                    // и модуль «Куда сходить» открывался только через навигацию.
                                    // Тот же экран, что и PATHS.whereToGo — «Куда сходить» теперь
                                    // доступен и по прямой ссылке изнутри приложения.
                                    {path: PATHS.appWhereToGo, element: tabPage(<WhereToGoPage/>)},
                                ],
                            },
                            {path: PATHS.whereToGo, element: tabPage(<WhereToGoPage/>)},
                        ],
                    },
                ],
            },
            {path: PATHS.registerComplete, element: <CompleteRegistrationRoute/>},
            {
                path: PATHS.app,
                children: [
                    {path: PATHS.appAnalysisHistory, element: page(<AnalysisHistoryPage/>)},
                    {path: PATHS.appAnalysis.pattern, element: <AnalysisDeepLink/>},
                ],
            },
            {path: PATHS.paymentConfirm, element: page(<PaymentConfirmPage/>)},
            {path: PATHS.support, element: page(<SupportPage/>)},
        ],
    },
    // Легаси-адрес редактирования профиля — теперь это модалка внутри приложения.
    {path: PATHS.profileEdit, element: <Navigate to={PATHS.app} replace/>},
];

/**
 * Набор роутов для admin.forruss.ru: без лендинга, регистрации, приложения.
 * Только логин (если не аутентифицирована сессия) и сама админ-панель.
 */
const adminSubdomainRoutes: RouteObject[] = [
    {
        path: PATHS.landing,
        element: <><ScrollToTop/><Outlet/></>,
        children: [
            {index: true, element: page(<AdminStandalonePage/>)},
            // Любой неизвестный путь на админ-поддомене — на корень.
            // Раньше catch-all отсутствовал: React Router показывал
            // встроенную страницу 404, когда пользователь попадал на
            // /app (по ссылке «В приложение» или случайно).
            {path: "*", element: <Navigate to={PATHS.landing} replace/>},
        ],
    },
];

/**
 * Кабинет компаний. Сессия компании отдельная от пользовательской, поэтому и
 * гарды свои: раньше /company/dashboard и формы мест открывались вообще без
 * проверки — достаточно было знать адрес.
 */
const companyRoutes: RouteObject[] = [
    {
        // Провайдер сессии — общий родитель обеих веток, иначе состояние
        // теряется на переходе «вход → кабинет» (см. CompanyRoute.tsx).
        element: <CompanyAuthLayout/>,
        children: [
            {
                element: <CompanyGuestRoute/>,
                children: [
                    {path: PATHS.company.login, element: page(<CompanyLoginPage/>)},
                    {path: PATHS.company.register, element: page(<CompanyRegisterPage/>)},
                ],
            },
            {
                element: <CompanyProtectedRoute/>,
                children: [
                    {path: PATHS.company.dashboard, element: page(<CompanyDashboardPage/>)},
                    {path: PATHS.company.placesNew, element: page(<CompanyPlaceFormPage/>)},
                    {path: PATHS.company.placesEditPattern, element: page(<CompanyPlaceFormPage/>)},
                    {path: PATHS.company.placementStatusPattern, element: page(<CompanyPlacementStatusPage/>)},
                ],
            },
        ],
    },
];

/** Общедоступные страницы: QR-сканы, приглашения, юридические документы, куда сходить. */
const publicRoutes: RouteObject[] = [
    {path: PATHS.scan.pattern, element: page(<ScanProfile/>)},
    {path: PATHS.invite.pattern, element: page(<InvitePage/>)},
    // Страницы юридических документов — по одной на каждый документ из конфига.
    ...(Object.keys(DOCS) as DocKey[]).map((doc) => ({
        path: DOCS[doc].path,
        element: page(<LegalDocumentPage doc={doc}/>),
    })),
];

/** Определяем поддомен при загрузке — только на клиенте. */
const isAdminSubdomain =
    typeof window !== "undefined" &&
    (window.location.hostname === "admin.forruss.ru" ||
     window.location.hostname === "dev-admin.forruss.ru");

export const router = isAdminSubdomain
    ? createBrowserRouter(adminSubdomainRoutes)
    : createBrowserRouter([
        {
            path: PATHS.landing,
            element: <><ScrollToTop/><Outlet/></>,
            children: [
                ...onboardingRoutes,
                ...appRoutes,
                // Админ-роуты на основном приложении отсутствуют — ни для какой роли.
                // Админ-панель доступна только через admin.forruss.ru.
                ...companyRoutes,
                ...publicRoutes,
                {path: "*", element: <Navigate to={PATHS.landing} replace/>},
            ],
        },
    ]);
